// モデル管理のためのユーティリティクラス

import type { TrainedModel, ModelType } from '../../types/model.types';
import type { MIDASProject } from '../../types/project.types';
import { createModelId } from '../../types';
import { checkModelNameDuplicate, DuplicateCheckResult } from './duplicateNameChecker';
import logger from './logger';

export class ModelManager {
  private project: MIDASProject;

  constructor(project: MIDASProject) {
    this.project = project;
    // プロジェクトが古い形式の場合の初期化
    if (!this.project.models) {
      this.project.models = {};
    }
  }

  /**
   * モデルを保存する
   */
  saveModel(model: TrainedModel): void {
    this.project.models[model.id] = model;
  }

  /**
   * モデル名の重複をチェックする
   */
  checkModelNameDuplicate(name: string): DuplicateCheckResult {
    return checkModelNameDuplicate(name, this.project);
  }

  /**
   * モデルを取得する
   */
  getModel(modelId: string): TrainedModel | undefined {
    return this.project.models[createModelId(modelId)];
  }

  /**
   * すべてのモデルを取得する
   */
  getAllModels(): TrainedModel[] {
    return Object.values(this.project.models);
  }

  /**
   * 特定のタイプのモデルを取得する
   */
  getModelsByType(type: ModelType): TrainedModel[] {
    return this.getAllModels().filter(model => model.type === type);
  }

  /**
   * 特定のデータセットで学習されたモデルを取得する
   */
  getModelsByDataset(datasetId: string): TrainedModel[] {
    return this.getAllModels().filter(model => model.metadata.trainingDatasetId === datasetId);
  }

  /**
   * モデルを削除する
   * GLMモデルの場合、関連する診断データセットも自動的に削除する
   */
  deleteModel(modelId: string): boolean {
    logger.info('[ModelManager] deleteModel called:', { modelId });

    const model = this.project.models[createModelId(modelId)];
    if (!model) {
      logger.warn('[ModelManager] Model not found:', { modelId });
      return false;
    }

    logger.info('[ModelManager] Model found:', {
      modelId,
      modelName: model.name,
      modelType: model.type
    });

    // GLMモデルで診断データセットがある場合は自動削除
    if (model.type === 'glm') {
      const glmParameters = model.parameters as import('../../types/model.types').GLMParameters;
      if (glmParameters.diagnosticSummary?.diagnosticDatasetId) {
        const diagnosticDatasetId = glmParameters.diagnosticSummary.diagnosticDatasetId;
        logger.info('[ModelManager] Attempting to delete diagnostic dataset:', { diagnosticDatasetId });

        if (this.project.datasets[diagnosticDatasetId]) {
          delete this.project.datasets[diagnosticDatasetId];
          logger.info('[ModelManager] Diagnostic dataset deleted:', { diagnosticDatasetId });
        } else {
          logger.warn('[ModelManager] Diagnostic dataset not found:', { diagnosticDatasetId });
        }
      }
    }

    delete this.project.models[createModelId(modelId)];
    logger.info('[ModelManager] Model deleted successfully:', { modelId });
    return true;
  }

  /**
   * モデルの名前を更新する
   */
  updateModelName(modelId: string, newName: string): boolean {
    const model = this.getModel(modelId);
    if (!model) {
      return false;
    }
    model.name = newName;
    return true;
  }

  /**
   * プロジェクトを取得する
   */
  getProject(): MIDASProject {
    return this.project;
  }


  /**
   * プロジェクトをシリアライズ可能な形式に変換する
   */
  toSerializable(): Pick<MIDASProject, 'models'> {
    return {
      models: this.project.models
    };
  }

  /**
   * シリアライズされたデータからモデルを復元する
   */
  static fromSerializable(project: MIDASProject, data: Pick<MIDASProject, 'models'>): void {
    project.models = data.models || {};
  }
}